/**
 * Interactive hover tooltip for chart-style graphs, in the web preview only. The exported SVG
 * stays static (GitHub README images cannot run scripts); here we reuse the renderer's layout
 * to map the pointer to the nearest data point / bucket.
 */
import { ComboLayout } from '../src/graph/combo/layout';
import { formatCompact } from '../src/graph/shared/aggregate';
import { formatNumber } from '../src/graph/shared/escape';
import { LineLayout } from '../src/graph/line/layout';
import { byId } from './dom';

export interface HoverTarget {
    x: number;
    /** Where the marker dot goes (null = no dot). */
    y: number | null;
    /** Tooltip anchor; defaults to `y`. */
    anchorY: number;
    title: string;
    lines: string[];
}

/** Everything the tooltip needs, in SVG user units. */
export interface HoverModel {
    width: number;
    plot: { left: number; right: number; top: number; bottom: number };
    targets: HoverTarget[];
    /** Width of the highlighted column (bar graphs); 0 = none. */
    band: number;
}

export const lineHoverModel = (layout: LineLayout): HoverModel => ({
    width: layout.width,
    plot: layout.plot,
    band: 0,
    targets: layout.points.map((p) => ({
        x: p.x,
        y: p.y,
        anchorY: p.y,
        title: p.title,
        lines: [`${formatNumber(p.count)} contribution${p.count === 1 ? '' : 's'}`],
    })),
});

export const comboHoverModel = (layout: ComboLayout): HoverModel => ({
    width: layout.width,
    plot: layout.plot,
    band: layout.showBars ? layout.slot : 0,
    targets: layout.items.map((item) => ({
        x: item.x,
        y: layout.showLine ? item.lineY : null,
        anchorY: Math.min(
            layout.showLine ? item.lineY : Infinity,
            layout.showBars ? item.bar.y : Infinity,
        ),
        title: item.title,
        lines: [
            ...(layout.showBars ? [`${layout.barLabel}: ${formatCompact(item.barValue)}`] : []),
            ...(layout.showLine ? [`${layout.lineLabel}: ${formatCompact(item.lineValue)}`] : []),
        ],
    })),
});

const nearest = (targets: HoverTarget[], x: number): HoverTarget => {
    let low = 0;
    let high = targets.length - 1;
    while (high - low > 1) {
        const mid = (low + high) >> 1;
        if (targets[mid].x < x) low = mid;
        else high = mid;
    }
    return Math.abs(targets[low].x - x) <= Math.abs(targets[high].x - x)
        ? targets[low]
        : targets[high];
};

export const createChartTooltip = (stage: HTMLElement, preview: HTMLElement) => {
    const tooltip = byId('chart-tooltip');
    const marker = byId('chart-marker');
    const band = byId('chart-band');
    const titleText = byId('chart-tooltip-date');
    const bodyText = byId('chart-tooltip-count');
    let model: HoverModel | null = null;

    const hide = () => {
        tooltip.hidden = true;
        marker.hidden = true;
        band.hidden = true;
    };

    const show = (event: PointerEvent) => {
        const svg = preview.querySelector('svg');
        if (!model || !svg || model.targets.length === 0) return hide();
        const box = svg.getBoundingClientRect();
        const scale = box.width / model.width;
        const x = (event.clientX - box.left) / scale;
        const y = (event.clientY - box.top) / scale;
        const { plot } = model;
        const margin = 16;
        if (
            x < plot.left - margin ||
            x > plot.right + margin ||
            y < plot.top - 40 ||
            y > plot.bottom + margin
        )
            return hide();

        const target = nearest(model.targets, x);
        const stageBox = stage.getBoundingClientRect();
        const toStageX = (value: number) => box.left - stageBox.left + value * scale;
        const toStageY = (value: number) => box.top - stageBox.top + value * scale;
        const left = toStageX(target.x);
        const top = toStageY(target.anchorY);

        titleText.textContent = target.title;
        bodyText.textContent = target.lines.join('\n');

        if (target.y !== null) {
            marker.style.left = `${left}px`;
            marker.style.top = `${toStageY(target.y)}px`;
            marker.hidden = false;
        } else {
            marker.hidden = true;
        }
        if (model.band > 0) {
            band.style.left = `${toStageX(target.x - model.band / 2)}px`;
            band.style.width = `${model.band * scale}px`;
            band.style.top = `${toStageY(plot.top)}px`;
            band.style.height = `${(plot.bottom - plot.top) * scale}px`;
            band.hidden = false;
        } else {
            band.hidden = true;
        }

        tooltip.style.left = `${Math.min(Math.max(left, 90), stageBox.width - 90)}px`;
        tooltip.style.top = `${top}px`;
        tooltip.classList.toggle('is-below', top < 70);
        tooltip.hidden = false;
    };

    preview.addEventListener('pointermove', show);
    preview.addEventListener('pointerleave', hide);

    return {
        /** Pass the hover model for the current graph (or null for graphs without one). */
        update(next: HoverModel | null, markerColor: string) {
            model = next;
            marker.style.background = markerColor;
            if (!next) hide();
        },
    };
};
